
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertCircle, Info, Undo, RotateCcw } from 'lucide-react';

export interface EnhancedToastAction {
  label: string;
  action: () => void;
  variant?: 'default' | 'destructive' | 'outline';
}

export interface EnhancedToastOptions {
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
  action?: EnhancedToastAction;
  undoAction?: () => void;
  retryAction?: () => void;
  duration?: number;
}

export const useEnhancedToast = () => {
  const { toast } = useToast();

  const showToast = ({
    title,
    description,
    variant = 'default',
    action,
    undoAction,
    retryAction,
    duration = 5000
  }: EnhancedToastOptions) => {
    const getIconTitle = () => {
      switch (variant) {
        case 'success':
          return `✓ ${title}`;
        case 'error':
          return `✗ ${title}`;
        case 'warning':
          return `⚠ ${title}`;
        case 'info':
          return `ℹ ${title}`;
        default:
          return title;
      }
    };

    const toastVariant = variant === 'error' ? 'destructive' : 'default';

    toast({
      title: getIconTitle(),
      description,
      variant: toastVariant,
      duration,
      action: (
        <div className="flex gap-2">
          {undoAction && (
            <Button
              variant="outline"
              size="sm"
              onClick={undoAction}
              className="h-8"
            >
              <Undo className="h-3 w-3 mr-1" />
              Undo
            </Button>
          )}
          {retryAction && (
            <Button
              variant="outline"
              size="sm"
              onClick={retryAction}
              className="h-8"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          )}
          {action && (
            <Button
              variant={action.variant || 'default'}
              size="sm"
              onClick={action.action}
              className="h-8"
            >
              {action.label}
            </Button>
          )}
        </div>
      )
    });
  };

  return { showToast };
};
